import {usePanelFilter} from '../components/dashboards/entities/DashboardEntityContext';

export type DashboardDates = {date_start: string; date_end: string};

/**
 * Returns the dashboard-wide date range when this panel participates and
 * hasn't been detached by the user choosing their own date. Returns null when
 * the panel is detached or the dashboard has no active range.
 */
export const useDashboardDates = (
  detachedFromDashboardDate?: boolean | null
): DashboardDates | null => {
  const {filter} = usePanelFilter();
  if (detachedFromDashboardDate || !filter?.params?.date_start) {
    return null;
  }
  return {
    date_start: filter.params.date_start as string,
    date_end: filter.params.date_end as string,
  };
};

/**
 * Injects dashboard dates into every item in an openedItems array that has
 * `params.date_start`/`date_end`. Returns the **original array reference**
 * when no items changed — critical for React's referential stability check
 * that gates the params dep on child grid useMemos (prevents a refetch loop).
 */
export const injectDashboardDates = <
  T extends {
    params?: {date_start?: string | null; date_end?: string | null} | null;
  },
>(
  items: T[],
  dates: DashboardDates
): T[] => {
  let anyChanged = false;
  const newItems = items.map((item) => {
    if (
      item.params?.date_start === dates.date_start &&
      item.params?.date_end === dates.date_end
    ) {
      return item;
    }
    anyChanged = true;
    return {
      ...item,
      params: {
        ...item.params,
        date_start: dates.date_start,
        date_end: dates.date_end,
      },
    };
  });
  return anyChanged ? newItems : items;
};
